import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DotDayMonthYearFormat, GERMAN_DATE_FORMAT, ISODateTimeStartOfDayFormat, ISODateTimeWithZeroSecondsFormat } from '@app/shared/constants';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { BuildingEfficiencyWizardStore } from '../building-efficiency-wizard.component.store';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import * as moment from 'moment';
import { isNil } from 'lodash';
import { FormControl } from '@angular/forms';
import { ElectricityMeterSelectionService } from '../electricity-meter-selection/electricity-meter-selection.service';
import { DateUtilities } from '@app/shared/date-utility';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-time-period-selection',
  templateUrl: './time-period-selection.component.html',
  styleUrls: ['./time-period-selection.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: GERMAN_DATE_FORMAT },
    LocalNumberPipe,
  ],
})
export class TimePeriodSelectionComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Output() nextButtonStatus = new EventEmitter<boolean>();
  @Output() isPeriodChanged = new EventEmitter<boolean>();
  @Input() savedEAReferenceData: any;

  data: IRawWizardData | null = null;
  isValidPeriod = true;
  floorArea: null | number | string = null;

  isValid = true;
  currentDate = moment(new Date());
  datePickerMinDate = moment(new Date()).subtract(5, 'years').startOf('month');
  datePickerMaxDate = moment(new Date()).subtract(1, 'years');
  startDatePicker = new FormControl();
  displayEndDate: any;
  note = this.translate.instant('module.shared.buildingEfficiencyWizard.step1.noteText');

  constructor(
    public overlayLoaderService: OverlayLoaderService,
    public translate: TranslateService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private electricityMeterSelection: ElectricityMeterSelectionService,
    private localNumberPipe: LocalNumberPipe
  ) {}

  public ngOnInit(): void {
    this._buildingEfficiencyWizardStore.wizardData$.pipe(takeUntil(this.unsubscribe$)).subscribe((data: IRawWizardData | null) => {
      this.data = data;
      if (!isNil(this.data)) {
        this.floorArea = this.data.buildingEfficiencyData.floorArea;
        if (!isNil(this.data.userDefinedStartDate) && !isNil(this.data.userDefinedEndDate)) {
          this.startDatePicker.setValue(moment(this.data.userDefinedStartDate));
          this.displayEndDate = this.getDisplayDate(this.data.userDefinedEndDate);
        }
        this.getNextButtonStatus();
        this.blur();
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public selectedMonthHandler(normalizedMonth: any, datepicker: any): void {
    const ctrlValue = isNil(this.startDatePicker.value) ? this.datePickerMinDate : this.startDatePicker.value;
    ctrlValue.year(normalizedMonth.year());
    ctrlValue.month(normalizedMonth.month());
    this.startDatePicker.setValue(ctrlValue);
    datepicker.close();

    if (!isNil(this.data)) {
      this.displayEndDate = this.getDisplayDate(this.data?.userDefinedEndDate);
      this.validatePeriod();
      this.data.hasTimePeriod = true;
    }
    this._buildingEfficiencyWizardStore.update(this.data);
  }

  public dateChanged(): void {
    if (!isNil(this.data)) {
      this.data.userDefinedStartDate = moment(new Date(this.startDatePicker.value)).startOf('month').format(ISODateTimeStartOfDayFormat);
      this.data.userDefinedEndDate = moment(new Date(this.startDatePicker.value)).add(11, 'months').endOf('month').format(ISODateTimeStartOfDayFormat);
    }
    this._buildingEfficiencyWizardStore.heatSelections = [];
    this.checkIfPeriodChanged();
  }

  public floorAreaChange(): void {
    if (typeof this.floorArea === 'string') {
      const emptyFloorAreaInput = '';
      if (this.floorArea === emptyFloorAreaInput) {
        this.floorArea = null;
      } else {
        this.floorArea = parseInt(this.floorArea, 10);
      }
    }
    if (this.data?.buildingEfficiencyData) {
      this.data.buildingEfficiencyData.floorArea = this.floorArea as number;
    }
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
    this.getNextButtonStatus();
  }

  public focus(): void {
    if (!isNil(this.floorArea)) {
      this.floorArea = parseInt(this.floorArea.toString().replace(',0', '').split(',').join('').split('.').join(''), 10);
    }
  }

  public blur(): void {
    if (!isNil(this.floorArea)) {
      this.floorArea = this.localNumberPipe.transform(this.floorArea);
    }
  }

  public numberOnly(event: KeyboardEvent): boolean {
    const key = event.key;
    return key === 'Backspace' || key === 'Delete' || key === 'Tab' || (key >= '0' && key <= '9');
  }

  public getDisplayDate(date: string | Date): string {
    return DateUtilities.toExpectedFormat(date, DotDayMonthYearFormat);
  }

  public onSelectionChange(isDefaultDate: boolean): void {
    if (!isNil(this.data)) {
      this.data.hasTimePeriod = isDefaultDate;
    }
    this.validatePeriod();
    this._buildingEfficiencyWizardStore.update(this.data);
    this._buildingEfficiencyWizardStore.heatSelections = [];
    this.checkIfPeriodChanged();
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
  }

  private getNextButtonStatus(): void {
    const floorArea = this.data?.buildingEfficiencyData?.floorArea;
    const nextButtonStatus = !this.isValidPeriod || isNil(floorArea) || floorArea < 1;

    this.nextButtonStatus.emit(nextButtonStatus);
  }

  private checkIfPeriodChanged(): void {
    this.isPeriodChanged.emit(false);
    const startDate = this.data?.hasTimePeriod ? this.data.userDefinedStartDate : this.data?.defaultStartDate;
    const dateToday = moment().format(ISODateTimeWithZeroSecondsFormat);

    if (this.savedEAReferenceData.efficiencyAnalysisStartDate !== startDate) {
      this.applyNewPeriod();
    } else {
      this.savedEAReferenceData.modifyDate !== dateToday && this.electricityMeterSelection.keyPressed
        ? this.applyNewPeriod()
        : this.isPeriodChanged.emit(false);
    }
  }

  private applyNewPeriod(): void {
    this.isPeriodChanged.emit(true);
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
    this.electricityMeterSelection.setIsConsumptionAutoSuggested(true);
  }

  private validatePeriod(): void {
    if (!isNil(this.data)) {
      if (this.data.hasTimePeriod) {
        this.isValidPeriod = moment(moment(this.data.userDefinedEndDate).format(ISODateTimeStartOfDayFormat)).isSameOrBefore(this.currentDate);
      } else {
        this.isValidPeriod = moment(moment(this.data.defaultEndDate).format(ISODateTimeStartOfDayFormat)).isSameOrBefore(this.currentDate);
      }
    }
  }
}
